/**
 * Phase 15.12 — Statutory Compliance + Insurance Register
 * Path: /admin/compliance
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Upload, Plus, Download,
  RefreshCw, Trash2, FileCheck, ExternalLink, Calendar, Search,
} from "lucide-react";
import { toast } from "sonner";

const LOCATIONS = ["Corporate", "Sitapura Unit", "Bassi Unit"];
const FREQUENCIES = ["Annual", "Half-Yearly", "Quarterly", "Monthly",
                       "Fortnightly", "Weekly", "Daily", "Event", "As needed"];
const RISKS = ["High", "Medium", "Low"];

const fmtMoney = (n) => "₹" + Math.round(Number(n || 0)).toLocaleString("en-IN");

const sevBg = {
  red: "bg-red-50 text-red-700 ring-red-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};

export default function CompliancePage() {
  const [tab, setTab] = useState("compliance");
  const [dash, setDash] = useState(null);

  const loadDash = async () => {
    try {
      const r = await api.get("/compliance/dashboard");
      setDash(r.data || null);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadDash(); }, [tab]);

  return (
    <div className="space-y-5" data-testid="compliance-page">
      <div className="flex items-start gap-3">
        <ShieldCheck className="h-7 w-7 text-indigo-600 mt-1" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            Phase 15.12 · Statutory Compliance
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Compliance Register
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            One living tracker for every licence, return, payment &amp; insurance renewal
            across Corporate, Sitapura Unit and Bassi Unit.
          </p>
        </div>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <DashTile label="Compliance Overdue"
                    value={dash.compliance?.OVERDUE ?? 0}
                    note={`of ${dash.compliance?.total ?? 0}`}
                    tone="red" />
          <DashTile label="Compliance Due Soon"
                    value={dash.compliance?.["DUE SOON"] ?? 0}
                    tone="amber" />
          <DashTile label="Insurance Expired"
                    value={dash.insurance?.EXPIRED ?? 0}
                    note={`of ${dash.insurance?.total ?? 0}`}
                    tone="red" />
          <DashTile label="Insurance Renew Soon"
                    value={dash.insurance?.["RENEW SOON"] ?? 0}
                    tone="amber" />
          <DashTile label="Upcoming (30d)"
                    value={(dash.upcoming_30_days || []).length}
                    tone="blue" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="compliance" data-testid="tab-compliance-register">
            Compliance Register
          </TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance-register">
            Insurance Register
          </TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-compliance-dashboard">
            Dashboard
          </TabsTrigger>
        </TabsList>
        <TabsContent value="compliance" className="mt-4">
          <RegisterTab kind="compliance" onChange={loadDash} />
        </TabsContent>
        <TabsContent value="insurance" className="mt-4">
          <RegisterTab kind="insurance" onChange={loadDash} />
        </TabsContent>
        <TabsContent value="dashboard" className="mt-4">
          <DashboardTab dash={dash} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DashTile({ label, value, note, tone }) {
  const cls = {
    red: "text-red-700", amber: "text-amber-700",
    green: "text-emerald-700", blue: "text-blue-700",
  }[tone] || "text-slate-700";
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${cls}`}>{value}</div>
      {note && <div className="text-[10px] text-slate-400 mt-0.5">{note}</div>}
    </div>
  );
}

function RegisterTab({ kind, onChange }) {
  const isCompliance = kind === "compliance";
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({
    location: "", status: "", category: "", risk: "", owner: "", q: "",
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = { type: kind };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/compliance/items", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [JSON.stringify(filters), kind]);

  const onDelete = async (r) => {
    const label = isCompliance ? r.obligation : r.policy_type;
    if (!window.confirm(`Delete "${label}" (${r.location})?`)) return;
    try {
      await api.delete(`/compliance/items/${r.id}`, { params: { type: kind } });
      toast.success("Deleted");
      load(); onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const markCompleted = async (r) => {
    try {
      await api.post(`/compliance/items/${r.id}/complete`,
        {}, { params: { type: kind } });
      toast.success(isCompliance ? "Marked completed · Next Due rolled"
                                    : "Marked renewed · Policy Expiry rolled +1y");
      load(); onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadProof = async (r, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/compliance/items/${r.id}/upload-proof`, fd, {
        params: { type: kind },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Proof uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const importXlsx = async (file) => {
    if (!file) return;
    setImportBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("default_location", filters.location || "Corporate");
    try {
      const r = await api.post("/compliance/import-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const c = r.data?.compliance || {};
      const i = r.data?.insurance || {};
      toast.success(
        `Compliance: +${c.created || 0} / Δ${c.updated || 0} · ` +
        `Insurance: +${i.created || 0} / Δ${i.updated || 0}`,
      );
      load(); onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setImportBusy(false); }
  };

  const downloadSample = async () => {
    try {
      const r = await api.get("/compliance/sample-template",
                              { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "compliance_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const uniqueCategories = useMemo(() =>
    Array.from(new Set(rows.map(r => r.category).filter(Boolean))).sort(),
    [rows]);
  const uniqueOwners = useMemo(() =>
    Array.from(new Set(rows.map(r => r.owner).filter(Boolean))).sort(),
    [rows]);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">
              Search
            </Label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input className="pl-7" value={filters.q}
                     onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                     placeholder={isCompliance
                                  ? "Obligation / authority / ref…"
                                  : "Policy / insurer / no…"}
                     data-testid="comp-filter-q" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Location</Label>
            <Select value={filters.location || "all"}
                    onValueChange={(v) => setFilters((f) => ({ ...f, location: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-[160px]" data-testid="comp-filter-location">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Status</Label>
            <Select value={filters.status || "all"}
                    onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-[140px]" data-testid="comp-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {isCompliance ? (
                  <>
                    <SelectItem value="OVERDUE">OVERDUE</SelectItem>
                    <SelectItem value="DUE SOON">DUE SOON</SelectItem>
                    <SelectItem value="OK">OK</SelectItem>
                    <SelectItem value="NO DATE">NO DATE</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="EXPIRED">EXPIRED</SelectItem>
                    <SelectItem value="RENEW SOON">RENEW SOON</SelectItem>
                    <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                    <SelectItem value="NO DATE">NO DATE</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          {isCompliance && uniqueCategories.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Category</Label>
              <Select value={filters.category || "all"}
                      onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {uniqueCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={downloadSample}
                  data-testid="comp-sample-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Sample XLSX
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".xlsx,.xlsm" className="hidden"
                   disabled={importBusy}
                   onChange={(e) => { importXlsx(e.target.files?.[0]); e.target.value = ""; }}
                   data-testid="comp-import-input" />
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md
                              ring-1 ring-slate-200 bg-white text-xs font-semibold hover:bg-slate-50">
              <Upload className="h-3.5 w-3.5" />
              {importBusy ? "Importing…" : "Import XLSX"}
            </span>
          </label>
          <Button size="sm" variant="outline" onClick={load} disabled={busy}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Reload
          </Button>
          <div className="flex-1" />
          <Button size="sm" className="brand-bg brand-bg-hover text-white"
                  onClick={() => { setEditing(null); setOpen(true); }}
                  data-testid="comp-add-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add {isCompliance ? "Compliance" : "Policy"}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto"
           data-testid={`comp-table-${kind}`}>
        <table className="w-full min-w-[1000px] text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500
                              sticky top-0 z-10 shadow-sm">
            {isCompliance ? (
              <tr>
                <th className="text-left px-2 py-2">Location</th>
                <th className="text-left px-2 py-2">Category</th>
                <th className="text-left px-2 py-2">Obligation</th>
                <th className="text-left px-2 py-2">Authority</th>
                <th className="text-left px-2 py-2">Frequency</th>
                <th className="text-left px-2 py-2">Last Completed</th>
                <th className="text-left px-2 py-2">Next Due</th>
                <th className="text-center px-2 py-2">Status</th>
                <th className="text-left px-2 py-2">Owner</th>
                <th className="text-center px-2 py-2">Risk</th>
                <th className="text-center px-2 py-2">Proof</th>
                <th className="text-center px-2 py-2">Actions</th>
              </tr>
            ) : (
              <tr>
                <th className="text-left px-2 py-2">Location</th>
                <th className="text-left px-2 py-2">Policy Type</th>
                <th className="text-left px-2 py-2">Insurer / No.</th>
                <th className="text-right px-2 py-2">Sum Insured</th>
                <th className="text-right px-2 py-2">Premium</th>
                <th className="text-left px-2 py-2">Policy Start</th>
                <th className="text-left px-2 py-2">Policy Expiry</th>
                <th className="text-center px-2 py-2">Status</th>
                <th className="text-left px-2 py-2">Broker</th>
                <th className="text-center px-2 py-2">Proof</th>
                <th className="text-center px-2 py-2">Actions</th>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100"
                  data-testid={`comp-row-${r.id}`}>
                <td className="px-2 py-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded ring-1
                                    ring-slate-200 bg-slate-50 text-slate-700">
                    {r.location || "—"}
                  </span>
                </td>
                {isCompliance ? (
                  <>
                    <td className="px-2 py-2">{r.category || "—"}</td>
                    <td className="px-2 py-2 max-w-[260px]">
                      <div className="font-semibold text-slate-900">{r.obligation}</div>
                      {r.applicable_law && (
                        <div className="text-[10px] text-slate-500 truncate"
                             title={r.applicable_law}>{r.applicable_law}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-[11px]">{r.authority || "—"}</td>
                    <td className="px-2 py-2 text-[11px]">{r.frequency || "—"}</td>
                    <td className="px-2 py-2">{r.last_completed || "—"}</td>
                    <td className="px-2 py-2 font-semibold">{r.next_due || "—"}</td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-2 max-w-[200px]">
                      <div className="font-semibold text-slate-900">{r.policy_type}</div>
                      {r.coverage_notes && (
                        <div className="text-[10px] text-slate-500 truncate"
                             title={r.coverage_notes}>{r.coverage_notes}</div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div>{r.insurer || "—"}</div>
                      {r.policy_no && (
                        <div className="text-[10px] font-mono text-slate-500">{r.policy_no}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-mono">
                      {r.sum_insured ? fmtMoney(r.sum_insured) : "—"}
                    </td>
                    <td className="px-2 py-2 text-right font-mono">
                      {r.premium ? fmtMoney(r.premium) : "—"}
                    </td>
                    <td className="px-2 py-2">{r.policy_start || "—"}</td>
                    <td className="px-2 py-2 font-semibold">{r.policy_expiry || "—"}</td>
                  </>
                )}
                <td className="px-2 py-2 text-center">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold
                                     ring-1 ${sevBg[r.severity] || sevBg.slate}`}
                        data-testid={`comp-status-${r.id}`}>
                    {r.status || "—"}
                    {r.days_to_due != null && (
                      <span className="ml-1 opacity-70">
                        ({r.days_to_due >= 0 ? `${r.days_to_due}d` : `${Math.abs(r.days_to_due)}d ago`})
                      </span>
                    )}
                  </span>
                </td>
                {isCompliance ? (
                  <>
                    <td className="px-2 py-2 text-[11px]">{r.owner || "—"}</td>
                    <td className="px-2 py-2 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ring-1 ${
                        r.risk === "High" ? "bg-red-50 text-red-700 ring-red-200" :
                        r.risk === "Medium" ? "bg-amber-50 text-amber-800 ring-amber-200" :
                        r.risk === "Low" ? "bg-emerald-50 text-emerald-700 ring-emerald-200" :
                        "text-slate-400"
                      }`}>{r.risk || "—"}</span>
                    </td>
                  </>
                ) : (
                  <td className="px-2 py-2 text-[11px]">
                    {r.broker || "—"}
                    {r.broker_contact && (
                      <div className="text-[10px] text-slate-500">{r.broker_contact}</div>
                    )}
                  </td>
                )}
                <td className="px-2 py-2 text-center">
                  <ProofCell r={r} kind={kind} onUpload={uploadProof} />
                </td>
                <td className="px-2 py-2 text-center">
                  <div className="flex gap-1 justify-center">
                    <Button size="sm" variant="ghost"
                            onClick={() => markCompleted(r)}
                            title={isCompliance ? "Mark completed → roll Next Due"
                                                : "Mark renewed → roll Expiry +1y"}
                            data-testid={`comp-complete-${r.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    </Button>
                    <Button size="sm" variant="ghost"
                            onClick={() => { setEditing(r); setOpen(true); }}
                            data-testid={`comp-edit-${r.id}`}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600"
                            onClick={() => onDelete(r)}
                            data-testid={`comp-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !busy && (
              <tr><td colSpan={12} className="text-center py-8 text-xs text-slate-400">
                No {isCompliance ? "compliance items" : "policies"} yet. Click
                "Import XLSX" or "Add {isCompliance ? "Compliance" : "Policy"}".
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ItemDialog open={open} setOpen={setOpen} editing={editing} kind={kind}
                  onSaved={() => { load(); onChange?.(); }} />
    </div>
  );
}

function ProofCell({ r, kind, onUpload }) {
  const inputId = `proof-${kind}-${r.id}`;
  return (
    <div className="flex items-center justify-center gap-1">
      {r.proof_url ? (
        <a href={r.proof_url} target="_blank" rel="noreferrer"
           className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5"
           data-testid={`comp-proof-${r.id}`}>
          <FileCheck className="h-3 w-3" /> View
        </a>
      ) : (
        <span className="text-[10px] text-slate-400">—</span>
      )}
      <label htmlFor={inputId}
             className="text-[10px] underline cursor-pointer text-bravura-700">
        Upload
        <input id={inputId} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
               className="hidden"
               onChange={(e) => onUpload(r, e.target.files?.[0])} />
      </label>
    </div>
  );
}

function ItemDialog({ open, setOpen, editing, kind, onSaved }) {
  const isCompliance = kind === "compliance";
  const blank = isCompliance ? {
    location: "Corporate", category: "", obligation: "", applicable_law: "",
    authority: "", nature: "", frequency: "Annual", reference_no: "",
    issued_on: "", last_completed: "", next_due: "", expiry: "",
    reminder_lead: 30, owner: "", reviewer: "", statutory_fee: "",
    penalty: "", risk: "Medium", remarks: "",
  } : {
    location: "Corporate", policy_type: "", insurer: "", policy_no: "",
    sum_insured: "", premium: "", policy_start: "", policy_expiry: "",
    premium_due: "", reminder_lead: 45, broker: "", broker_contact: "",
    coverage_notes: "", claim_history: "", owner: "", remarks: "",
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (editing) {
      const { id, created_at, updated_at, severity, status,
              days_to_due, ...rest } = editing;
      setForm({ ...blank, ...rest });
    } else {
      setForm(blank);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, kind, open]);

  const save = async () => {
    if (isCompliance && !form.obligation) {
      toast.warning("Obligation is required"); return;
    }
    if (!isCompliance && !form.policy_type) {
      toast.warning("Policy Type is required"); return;
    }
    try {
      const payload = { ...form };
      ["statutory_fee", "sum_insured", "premium"].forEach((k) => {
        if (payload[k] === "") delete payload[k];
        else if (payload[k] != null) payload[k] = Number(payload[k]);
      });
      if (editing) {
        await api.patch(`/compliance/items/${editing.id}`, payload,
                          { params: { type: kind } });
        toast.success("Updated");
      } else {
        await api.post("/compliance/items", payload, { params: { type: kind } });
        toast.success("Created");
      }
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit" : "Add"} {isCompliance ? "Compliance Item" : "Policy"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Location-scoped tracking with auto status (OVERDUE / DUE SOON / OK)
            based on dates and reminder lead.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-2">
          <div>
            <Label className="text-xs">Location *</Label>
            <Select value={form.location}
                    onValueChange={(v) => setForm({ ...form, location: v })}>
              <SelectTrigger data-testid="comp-form-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isCompliance ? (
            <>
              <div>
                <Label className="text-xs">Category</Label>
                <Input value={form.category}
                       onChange={(e) => setForm({ ...form, category: e.target.value })}
                       placeholder="e.g. Tax, Labour, Factory" />
              </div>
              <div className="md:col-span-1">
                <Label className="text-xs">Frequency</Label>
                <Select value={form.frequency || "Annual"}
                        onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Compliance / Obligation *</Label>
                <Input value={form.obligation}
                       onChange={(e) => setForm({ ...form, obligation: e.target.value })}
                       placeholder="e.g. ESI Half-yearly Return"
                       data-testid="comp-form-obligation" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Applicable Law</Label>
                <Input value={form.applicable_law}
                       onChange={(e) => setForm({ ...form, applicable_law: e.target.value })}
                       placeholder="e.g. ESI Act 1948" />
              </div>
              <div>
                <Label className="text-xs">Authority</Label>
                <Input value={form.authority}
                       onChange={(e) => setForm({ ...form, authority: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Last Completed</Label>
                <Input type="date" value={form.last_completed}
                       onChange={(e) => setForm({ ...form, last_completed: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Next Due *</Label>
                <Input type="date" value={form.next_due}
                       onChange={(e) => setForm({ ...form, next_due: e.target.value })}
                       data-testid="comp-form-next-due" />
              </div>
              <div>
                <Label className="text-xs">Reminder Lead (days)</Label>
                <Input type="number" value={form.reminder_lead}
                       onChange={(e) => setForm({ ...form, reminder_lead: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Owner</Label>
                <Input value={form.owner}
                       onChange={(e) => setForm({ ...form, owner: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Reviewer / Approver</Label>
                <Input value={form.reviewer}
                       onChange={(e) => setForm({ ...form, reviewer: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Risk</Label>
                <Select value={form.risk || "Medium"}
                        onValueChange={(v) => setForm({ ...form, risk: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RISKS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Statutory Fee (₹)</Label>
                <Input type="number" value={form.statutory_fee}
                       onChange={(e) => setForm({ ...form, statutory_fee: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Reference / Reg. No.</Label>
                <Input value={form.reference_no}
                       onChange={(e) => setForm({ ...form, reference_no: e.target.value })} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Penalty if Missed</Label>
                <Input value={form.penalty}
                       onChange={(e) => setForm({ ...form, penalty: e.target.value })} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Remarks</Label>
                <Textarea rows={2} value={form.remarks}
                          onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </div>
            </>
          ) : (
            <>
              <div className="md:col-span-2">
                <Label className="text-xs">Policy Type *</Label>
                <Input value={form.policy_type}
                       onChange={(e) => setForm({ ...form, policy_type: e.target.value })}
                       placeholder="e.g. Standard Fire & Special Perils"
                       data-testid="comp-form-policy-type" />
              </div>
              <div>
                <Label className="text-xs">Insurer</Label>
                <Input value={form.insurer}
                       onChange={(e) => setForm({ ...form, insurer: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Policy No.</Label>
                <Input value={form.policy_no}
                       onChange={(e) => setForm({ ...form, policy_no: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Sum Insured (₹)</Label>
                <Input type="number" value={form.sum_insured}
                       onChange={(e) => setForm({ ...form, sum_insured: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Premium (₹)</Label>
                <Input type="number" value={form.premium}
                       onChange={(e) => setForm({ ...form, premium: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Policy Start</Label>
                <Input type="date" value={form.policy_start}
                       onChange={(e) => setForm({ ...form, policy_start: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Policy Expiry *</Label>
                <Input type="date" value={form.policy_expiry}
                       onChange={(e) => setForm({ ...form, policy_expiry: e.target.value })}
                       data-testid="comp-form-policy-expiry" />
              </div>
              <div>
                <Label className="text-xs">Premium Due</Label>
                <Input type="date" value={form.premium_due}
                       onChange={(e) => setForm({ ...form, premium_due: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Reminder Lead (days)</Label>
                <Input type="number" value={form.reminder_lead}
                       onChange={(e) => setForm({ ...form, reminder_lead: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Broker</Label>
                <Input value={form.broker}
                       onChange={(e) => setForm({ ...form, broker: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Broker Contact</Label>
                <Input value={form.broker_contact}
                       onChange={(e) => setForm({ ...form, broker_contact: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Owner</Label>
                <Input value={form.owner}
                       onChange={(e) => setForm({ ...form, owner: e.target.value })} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Coverage / Exclusions</Label>
                <Textarea rows={2} value={form.coverage_notes}
                          onChange={(e) => setForm({ ...form, coverage_notes: e.target.value })} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Claim History</Label>
                <Input value={form.claim_history}
                       onChange={(e) => setForm({ ...form, claim_history: e.target.value })} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Remarks</Label>
                <Textarea rows={2} value={form.remarks}
                          onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} className="brand-bg brand-bg-hover text-white"
                  data-testid="comp-form-save">
            {editing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DashboardTab({ dash }) {
  if (!dash) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }
  const overdue = dash.overdue || [];
  const upcoming = dash.upcoming_30_days || [];
  const byLoc = dash.by_location_compliance || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {LOCATIONS.map((l) => {
          const x = byLoc[l] || { total: 0, red: 0, amber: 0 };
          return (
            <div key={l} className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                {l}
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-bold">{x.total}</span>
                <span className="text-[10px] text-slate-500">items</span>
              </div>
              <div className="flex gap-3 mt-1 text-[11px]">
                <span className="text-red-700">🔴 {x.red} overdue</span>
                <span className="text-amber-700">🟠 {x.amber} due soon</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <DashList title="🔴 Overdue / Expired" items={overdue} />
        <DashList title="🟠 Due in next 30 days" items={upcoming} />
      </div>
    </div>
  );
}

function DashList({ title, items }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 text-xs font-bold border-b">
        {title} <span className="text-slate-400 font-normal">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="p-4 text-xs text-slate-400 text-center">Nothing here ✓</div>
      ) : (
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {items.map((r) => (
            <div key={r.id} className="px-3 py-2 text-xs flex items-start gap-2">
              <Calendar className="h-3 w-3 mt-0.5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">
                  {r.obligation || r.policy_type}
                </div>
                <div className="text-[10px] text-slate-500">
                  {r.location} · {r.next_due || r.policy_expiry}
                  {r.days_to_due != null && (
                    <span className={r.days_to_due < 0 ? "text-red-700" : "text-amber-700"}>
                      {" · "}{r.days_to_due >= 0 ? `in ${r.days_to_due}d`
                                                    : `${Math.abs(r.days_to_due)}d ago`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
